import { useInfiniteQuery } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AssignmentsService } from '../../../../../js/api';
import { Opportunity as ApiOpportunity } from '../../../../../js/api/models/Opportunity';
import { useDocumentTitle } from '../../../../../js/app/hooks/use-document-title';
import { Button } from '../../../components/button';
import { Drawer, DrawerBody } from '../../../components/drawer';
import { Heading } from '../../../components/heading';
import { ListFilter } from '../../../components/list-filter';
import { QueueList } from '../../../components/queue-list';
import StatusPill from '../../../components/StatusPill';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSortHeader,
} from '../../../components/table';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsMaps } from '../../../hooks/useCodeDefinitions';
import { useInfiniteScroll } from '../../../hooks/useInfiniteScroll';
import { useListFilterParams } from '../../../hooks/useListFilterParams';
import { useRequireActingAsEntity } from '../../../hooks/useRequireActingAsEntity';
import { useStaffOrHasPermission } from '../../../hooks/useStaffOrHasPermission';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';
import { STICKY_HEADER } from '../../../utils/globalStyles';
import { MAIN_CONTENT_AREA, PAGE_HEADER } from '../../../utils/layoutStyles';
import { TRANSITION_ALL } from '../../../utils/transitionStyles';
import OpportunityCard from '../../Organization/Opportunities/OpportunityCard';
import { OpportunityDrawer } from '../../Organization/Opportunities/OpportunityDrawer';

const PAGE_SIZE = 21;
const DATE_FORMAT = 'LLL d, yyyy';
const QUEUE_CATEGORY = 'HealthcareEntityOpportunityQueueNames';
const TABLE_LINK_STYLES =
  'tw:text-brand-green-0 tw:hover:text-brand-green-1 tw:!no-underline tw:transition-colors tw:hover:border-1 tw:hover:rounded-md tw:hover:py-1 tw:hover:px-2 tw:hover:shadow-md';

type ApiParams = Parameters<typeof AssignmentsService.assignmentsOpportunitiesList>[0];
const CATEGORY_TO_API_PARAM: Record<string, keyof ApiParams> = {
  Specialty: 'specialties',
};

const CATEGORIES = Object.keys(CATEGORY_TO_API_PARAM) as readonly string[];

export type OpportunitiesListPageProps = {
  title?: string;
  queryKeyBase: string;
  displayTypePrefKey: string;
  showOrganization: boolean;
  showOrganizationFilter?: boolean;
  linkContext: 'organization' | 'staff';
};

export const OpportunitiesListPage: React.FC<OpportunitiesListPageProps> = ({
  title = 'Opportunities',
  queryKeyBase,
  displayTypePrefKey,
  showOrganization,
  showOrganizationFilter = false,
  linkContext,
  // eslint-disable-next-line sonarjs/cognitive-complexity
}) => {
  useDocumentTitle(title);
  const navigate = useNavigate();
  const { data: codeDefinitionMaps } = useCodeDefinitionsAsMaps([
    'Degree',
    'Specialty',
    'ShiftRateTypes',
    'ShiftRateTypeIcons',
  ]);

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedOpportunityIdFromUrl = searchParams.get('id');
  const [isDrawerOpen, setIsDrawerOpen] = useState(!!selectedOpportunityIdFromUrl);

  const selectedQueueCode: string | undefined = searchParams.get('queue') || undefined;

  const { filterValues, updateFilters } = useListFilterParams(searchParams, setSearchParams, {
    categories: CATEGORIES,
    displayTypePrefKey,
  });

  const search = filterValues.search || '';
  const statuses = filterValues.statuses || [];
  const categoryFilters = useMemo(() => filterValues.categories || {}, [filterValues.categories]);
  const healthcareEntity = filterValues.healthcare_entity || '';

  const displayType = (filterValues.displayType || 'card') as 'card' | 'table';
  const ordering = (filterValues.ordering || '-updated_at') as string;

  const canCreateOpp = useStaffOrHasPermission('create__assignments__opportunity');
  const requireActingAs = useRequireActingAsEntity();

  const setOrdering = useCallback(
    (next: string) => {
      updateFilters({
        search: filterValues.search,
        statuses: filterValues.statuses,
        reasons: filterValues.reasons,
        categories: filterValues.categories,
        displayType,
        ordering: next,
        healthcare_entity: filterValues.healthcare_entity,
      });
    },
    [
      updateFilters,
      filterValues.search,
      filterValues.statuses,
      filterValues.reasons,
      filterValues.categories,
      filterValues.healthcare_entity,
      displayType,
    ]
  );

  const apiQueueCode = selectedQueueCode ? (selectedQueueCode as ApiParams['queue']) : undefined;

  const categoryApiParams = useMemo(() => {
    const params: Partial<ApiParams> = {};
    Object.entries(categoryFilters).forEach(([category, values]) => {
      const apiParamName = CATEGORY_TO_API_PARAM[category];
      if (apiParamName && values.length > 0) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (params as Record<string, unknown>)[apiParamName] = values;
      }
    });
    return params;
  }, [categoryFilters]);

  const queryKey = useMemo(
    () => [
      queryKeyBase,
      selectedQueueCode,
      search,
      statuses,
      categoryFilters,
      ordering,
      healthcareEntity,
    ],
    [queryKeyBase, selectedQueueCode, search, statuses, categoryFilters, ordering, healthcareEntity]
  );

  const { data, isLoading, isFetchingNextPage, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey,
    queryFn: async ({ pageParam = 0 }) => {
      return AssignmentsService.assignmentsOpportunitiesList({
        queue: apiQueueCode,
        search: search || undefined,
        status: statuses.length > 0 ? (statuses as Array<string | null>) : undefined,
        healthcareEntityId: showOrganization ? healthcareEntity || undefined : undefined,
        ...categoryApiParams,
        ordering: ordering as ApiParams['ordering'],
        limit: PAGE_SIZE,
        offset: pageParam as number,
      });
    },
    getNextPageParam: (lastPage, allPages) => {
      if (lastPage.next) {
        return allPages.length * PAGE_SIZE;
      }
      return undefined;
    },
  });

  const opportunities = useMemo(() => {
    return data?.pages.flatMap((page) => page.results || []) || [];
  }, [data]);

  const totalCount = data?.pages[0]?.count || 0;

  const loadMoreOpportunities = React.useCallback(async () => {
    if (hasNextPage && !isFetchingNextPage) {
      await fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  const { loadingRef } = useInfiniteScroll(loadMoreOpportunities, hasNextPage || false, {
    enabled: hasNextPage && !isLoading,
  });

  useEffect(() => {
    const opportunityIdFromUrl = searchParams.get('id');
    if (opportunityIdFromUrl) {
      setIsDrawerOpen(true);
    } else if (isDrawerOpen) {
      setIsDrawerOpen(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleQueueChange = (queueCode: string) => {
    const newSearchParams = new URLSearchParams(searchParams);
    if (queueCode) newSearchParams.set('queue', queueCode);
    else newSearchParams.delete('queue');
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleOpportunityCardClick = (opportunity: ApiOpportunity) => {
    setIsDrawerOpen(true);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set('id', opportunity.id);
    setSearchParams(newSearchParams, { replace: true });
  };

  const handleCloseDrawer = () => {
    setIsDrawerOpen(false);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('id');
    setSearchParams(newSearchParams, { replace: true });
  };

  return (
    <>
      <div
        className="tw:flex tw:h-[calc(100vh-72px)] tw:overflow-hidden"
        data-testid="opportunities-list"
      >
        <div
          className={clsx(
            `tw:flex tw:flex-col ${TRANSITION_ALL} tw:overflow-hidden`,
            isDrawerOpen ? 'tw:w-3/5 tw:lg:w-3/5' : 'tw:w-full'
          )}
        >
          <main className={MAIN_CONTENT_AREA}>
            <div className={STICKY_HEADER}>
              <div className={PAGE_HEADER}>
                <Heading level={1} className="tw:!text-brand-green-0 tw:dark:!text-zinc-100">
                  <i className="far fa-fw fa-suitcase-medical tw:mr-1" />
                  {title}
                </Heading>
                {canCreateOpp && (
                  <Button
                    color="brandgreen1"
                    onClick={() =>
                      requireActingAs(() => navigate('/app/organization/opportunities/new'))
                    }
                    className="ms-3"
                  >
                    Create Opportunity
                  </Button>
                )}
              </div>

              <div className="tw:mt-4">
                <QueueList
                  categoryName={QUEUE_CATEGORY}
                  notificationScope={WorkflowScopes.Opportunity}
                  selectedQueue={selectedQueueCode || undefined}
                  priorityQueues={true}
                  onQueueChange={handleQueueChange}
                />
              </div>

              <div className="tw:mt-1 tw:group" data-drawer-open={isDrawerOpen ? 'true' : 'false'}>
                <ListFilter
                  show_search={true}
                  workflow_scope={WorkflowScopes.Opportunity}
                  categories={Array.from(CATEGORIES)}
                  showStatus={true}
                  showReason={false}
                  showOrganizationFilter={showOrganizationFilter}
                  showDisplayType={true}
                  sortOptions={[
                    { label: 'Last Updated', value: 'updated_at' },
                    { label: 'Created', value: 'created_at' },
                    { label: 'Starts On', value: 'starts_on' },
                    { label: 'Status Changed On', value: 'status_at' },
                    ...(showOrganization
                      ? [{ label: 'Organization', value: 'healthcare_entity__name' }]
                      : []),
                    { label: 'Facility', value: 'facility__name' },
                    { label: 'Location', value: 'facility__city' },
                    { label: 'Status', value: 'current_status__title' },
                    { label: '# Pending', value: 'pending_connections' },
                    { label: '# Accepted', value: 'accepted_connections' },
                    { label: '# Rejected', value: 'rejected_connections' },
                    { label: '# Canceled', value: 'canceled_connections' },
                    { label: 'Auto-Match', value: 'settings__auto_match' },
                  ]}
                  values={filterValues}
                  onFilterChange={updateFilters}
                />
              </div>
            </div>
            <div className="tw:mt-3">
              {isLoading && opportunities.length === 0 && (
                <div className="tw:text-center tw:py-8" data-testid="opportunities-loading">
                  Loading opportunities...
                </div>
              )}
              {!isLoading && opportunities.length === 0 ? (
                <div
                  className="tw-col-span-full tw:text-center tw:text-zinc-500 tw:py-8"
                  data-testid="opportunities-empty"
                >
                  No opportunities found.
                  <div className="tw:text-zinc-500 tw:italic tw:text-sm">
                    {showOrganization
                      ? 'Refine your search.'
                      : 'Refine your search or create a new opportunity.'}
                  </div>
                </div>
              ) : (
                <>
                  {opportunities.length > 0 && (
                    <div className="tw:text-right tw:text-xs tw:text-zinc-500 tw:my-1 tw:mr-1">
                      Showing {opportunities.length} of {totalCount} opportunities
                    </div>
                  )}
                  {displayType === 'card' ? (
                    <div className="tw:@container">
                      <div className="tw:grid tw:grid-cols-1 tw:gap-3 tw:@min-[513px]:grid-cols-2 tw:@min-[793px]:grid-cols-3">
                        {(() => {
                          const opportunityIdFromUrl = searchParams.get('id');
                          return opportunities.map((opportunity) => {
                            const isSelected = opportunity.id === opportunityIdFromUrl;
                            return (
                              <div
                                key={opportunity.id}
                                data-testid={`opportunity-card-${opportunity.id}`}
                                onClick={() => handleOpportunityCardClick(opportunity)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleOpportunityCardClick(opportunity);
                                  }
                                }}
                                role="button"
                                tabIndex={0}
                              >
                                <OpportunityCard
                                  opportunity={opportunity}
                                  degreeMap={codeDefinitionMaps?.Degree || {}}
                                  specialtyMap={codeDefinitionMaps?.Specialty || {}}
                                  shiftRateTypeMap={codeDefinitionMaps?.ShiftRateTypes || {}}
                                  shiftRateTypeIconMap={
                                    codeDefinitionMaps?.ShiftRateTypeIcons || {}
                                  }
                                  isSelected={isSelected}
                                  showOrganization={showOrganization}
                                />
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  ) : (
                    <Table striped dense>
                      <TableHead>
                        <TableSortHeader
                          sortKey="code"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Code
                        </TableSortHeader>
                        {showOrganization && (
                          <TableSortHeader
                            sortKey="healthcare_entity__name"
                            ordering={ordering}
                            onOrderingChange={setOrdering}
                          >
                            Organization
                          </TableSortHeader>
                        )}
                        <TableSortHeader
                          sortKey="facility__name"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Facility
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="facility__city"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Location
                        </TableSortHeader>
                        <TableHeader>Degrees</TableHeader>
                        <TableSortHeader
                          sortKey="specialty_display"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Specialty
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="current_status__title"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Status
                        </TableSortHeader>

                        <TableSortHeader
                          sortKey="accepted_connections"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          # Accepted
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="rejected_connections"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          # Rejected
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="pending_connections"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          # Pending
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="canceled_connections"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          # Canceled
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="settings__auto_match"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Auto-Match
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="updated_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Last Updated
                        </TableSortHeader>
                        <TableSortHeader
                          sortKey="created_at"
                          ordering={ordering}
                          onOrderingChange={setOrdering}
                        >
                          Created
                        </TableSortHeader>
                      </TableHead>
                      <TableBody>
                        {/* eslint-disable-next-line sonarjs/cognitive-complexity */}
                        {opportunities.map((opportunity) => {
                          const opportunityIdFromUrl = searchParams.get('id');
                          const isSelected = opportunity.id === opportunityIdFromUrl;
                          return (
                            <TableRow
                              key={opportunity.id}
                              data-testid={`opportunity-row-${opportunity.id}`}
                              onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (target.closest('a, button, [data-no-drawer]')) return;
                                handleOpportunityCardClick(opportunity);
                              }}
                              className={clsx(isSelected ? 'tw:!bg-brand-green-2' : '')}
                            >
                              <TableCell>{opportunity.code}</TableCell>
                              {showOrganization && (
                                <TableCell>{opportunity.healthcare_entity?.name || '--'}</TableCell>
                              )}
                              <TableCell>{opportunity.facility?.name || '--'}</TableCell>
                              <TableCell>
                                {getFacilityLocationDisplayValue(opportunity.facility) || '--'}
                              </TableCell>
                              <TableCell>{opportunity.degrees?.join(', ')}</TableCell>
                              <TableCell>{opportunity.specialty_display}</TableCell>
                              <TableCell>
                                <StatusPill
                                  scope={WorkflowScopes.ConnectedFacility}
                                  status={opportunity.current_status}
                                />
                              </TableCell>
                              <TableCell className="tw:text-center">
                                {Number(opportunity.accepted_connections) > 0 ? (
                                  <a
                                    href={`/app/organization/connections?queue=all&status=${StatusCodes.AWAIT_CLEAR}%2c${StatusCodes.PEND_FAC_REV}%2c${StatusCodes.PEND_CONFIRM}%2c${StatusCodes.ON_HOLD}%2c${StatusCodes.PROV_ACTION_REQ}%2c${StatusCodes.AWAIT_PRVLG}%2c${StatusCodes.ACTIVE}%2c${StatusCodes.PAUSED}%2c${StatusCodes.WITHDRAWN}%2c${StatusCodes.TERMINATED}&search=${opportunity.code}`}
                                    className={clsx(TABLE_LINK_STYLES)}
                                  >
                                    {opportunity.accepted_connections}
                                  </a>
                                ) : (
                                  (opportunity.accepted_connections ?? 0)
                                )}
                              </TableCell>
                              <TableCell className="tw:text-center">
                                {Number(opportunity.rejected_connections) > 0 ? (
                                  <a
                                    href={`/app/organization/connections?queue=closed&status=${StatusCodes.DECLINED}&search=${opportunity.code}`}
                                    className={clsx(TABLE_LINK_STYLES)}
                                  >
                                    {opportunity.rejected_connections}
                                  </a>
                                ) : (
                                  (opportunity.rejected_connections ?? 0)
                                )}
                              </TableCell>
                              <TableCell className="tw:text-center">
                                {Number(opportunity.pending_connections) > 0 ? (
                                  <a
                                    href={`/app/organization/connections?queue=awaiting_provider&status=${StatusCodes.REQUESTED}&search=${opportunity.code}`}
                                    className={clsx(TABLE_LINK_STYLES)}
                                  >
                                    {opportunity.pending_connections}
                                  </a>
                                ) : (
                                  (opportunity.pending_connections ?? 0)
                                )}
                              </TableCell>
                              <TableCell className="tw:text-center">
                                {Number(opportunity.canceled_connections) > 0 ? (
                                  <a
                                    href={`/app/organization/connections?queue=closed&status=${StatusCodes.CANCELED}&search=${opportunity.code}`}
                                    className={clsx(TABLE_LINK_STYLES)}
                                  >
                                    {opportunity.canceled_connections}
                                  </a>
                                ) : (
                                  (opportunity.canceled_connections ?? 0)
                                )}
                              </TableCell>
                              <TableCell
                                className={clsx(
                                  'tw:text-center',
                                  opportunity?.settings?.auto_match
                                    ? 'tw:text-stoplight-success'
                                    : 'tw:text-stoplight-danger'
                                )}
                              >
                                {opportunity?.settings?.auto_match ? 'On' : 'Off'}
                              </TableCell>
                              <TableCell>
                                {opportunity.updated_at
                                  ? format(parseISO(opportunity.updated_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                              <TableCell>
                                {opportunity.created_at
                                  ? format(parseISO(opportunity.created_at), DATE_FORMAT)
                                  : '-'}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  )}
                </>
              )}

              {hasNextPage && (
                <div ref={loadingRef} className="tw:flex tw:justify-center tw:py-8">
                  {isFetchingNextPage && (
                    <div className="tw:text-center tw:text-zinc-500">
                      Loading more opportunities...
                    </div>
                  )}
                </div>
              )}
            </div>
          </main>
        </div>

        <Drawer open={isDrawerOpen} onClose={handleCloseDrawer} width="3xl" mode="side-by-side">
          <DrawerBody className="tw:!text-sm/6">
            {selectedOpportunityIdFromUrl && (
              <OpportunityDrawer
                key={selectedOpportunityIdFromUrl}
                opportunityId={selectedOpportunityIdFromUrl}
                onClose={handleCloseDrawer}
                listQueryKeyBase={queryKeyBase}
                linkContext={linkContext}
              />
            )}
          </DrawerBody>
        </Drawer>
      </div>
    </>
  );
};
